import React, {
  useMemo, useCallback, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import {
  Row, Col, DatePicker, TreeSelect,
} from 'antd';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import moment from 'moment';

import DateRangePickerButtons from '../common/datepicker/DateRangePickerButtons';
import BorderlessButton from '../common/buttons/BorderlessButton';
import Colors from '../constants/Colors';
import { SCHEDULE_DATE_PICKER_TREE_OPTS } from './schedule.constants';

const getFormat = (viewType) => {
  switch (viewType) {
    case 'Day':
    case 'UserDay':
      return 'MMM Do YY';
    case 'UserWeekdays':
      return '[Weekdays of] MMM Do YY';
    case 'UserWeek':
    case 'Week':
    case 'UserBiWeekly':
      return '[Week of] MMM Do YY';
    case 'Month': return 'MMM YYYY';
    default: return 'MMM YYYY';
  }
};

function ScheduleDatePicker({
  viewType,
  onViewTypeChange,
  onDateChange,
  date,
  onVisibleChanged,
  typeMap,
}) {
  const company = useSelector((state) => state.settings.company);
  const hasRedesign = company?.settings?.uxRedesignPt1;
  const [pickerOpen, setPickerOpen] = useState(false);

  const pickerValue = useMemo(() => moment(date.toMillis()), [date]);
  const format = useMemo(() => getFormat(viewType), [viewType]);

  const onButtonPress = useCallback((left) => () => {
    let mod = viewType.toLowerCase();
    if (mod === 'userday') mod = 'day';
    if (mod === 'userweekdays' || mod === 'userweek') mod = 'week';
    const isBiWeekly = viewType === 'UserBiWeekly';
    const dateShift = isBiWeekly ? { week: 2 } : { [mod]: 1 };
    if (left) {
      onDateChange(date.minus(dateShift));
    } else {
      onDateChange(date.plus(dateShift));
    }
  }, [date, viewType, onDateChange]);

  const getFooter = useCallback(() => {
    let label = '';
    switch (typeMap[viewType]) {
      case typeMap.Week:
        label = 'This Week';
        break;
      case typeMap.Month:
        label = 'This Month';
        break;
      default:
        return null;
    }
    return (
      <BorderlessButton
        title={label}
        onClick={() => {
          onDateChange(DateTime.local());
          setPickerOpen(false);
        }}
        style={{
          color: hasRedesign ? Colors.BLUE_700 : Colors.ONTRACCR_RED,
        }}
      />
    );
  }, [viewType, onDateChange]);

  useEffect(() => {
    if (onVisibleChanged) onVisibleChanged(pickerOpen);
  }, [onVisibleChanged, pickerOpen]);

  const datePickerTreeOpts = useMemo(() => {
    const keys = new Set(Object.keys(typeMap));
    return SCHEDULE_DATE_PICKER_TREE_OPTS.filter(({ value, children }) => {
      if (keys.has(value)) return true;

      if (children) {
        return children.some(({ value: innerValue }) => keys.has(innerValue));
      }

      return false;
    });
  }, [typeMap]);

  return (
    <Row gutter={10}>
      <Col>
        <TreeSelect
          treeData={datePickerTreeOpts}
          defaultValue={SCHEDULE_DATE_PICKER_TREE_OPTS[1].value}
          value={viewType}
          style={{ width: 125 }}
          onSelect={(type) => onViewTypeChange(type)}
          dropdownMatchSelectWidth={250}
        />
      </Col>
      <Col>
        <DateRangePickerButtons
          left
          onClick={onButtonPress(true)}
        />
      </Col>
      <Col>
        <DatePicker
          className="schedule-date-picker"
          key={viewType}
          picker={typeMap[viewType]}
          onChange={onDateChange}
          value={pickerValue}
          format={format}
          allowClear={false}
          renderExtraFooter={getFooter}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          style={{ width: 150 }}
        />
      </Col>
      <Col>
        <DateRangePickerButtons
          onClick={onButtonPress(false)}
        />
      </Col>
    </Row>
  );
}

ScheduleDatePicker.propTypes = {
  viewType: PropTypes.string.isRequired,
  onViewTypeChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  date: PropTypes.instanceOf(DateTime).isRequired,
  onVisibleChanged: PropTypes.func,
  typeMap: PropTypes.shape({
    Day: PropTypes.string,
    Week: PropTypes.string,
    Month: PropTypes.string,
    UserDay: PropTypes.string,
    UserBiWeekly: PropTypes.string,
  }),
};

ScheduleDatePicker.defaultProps = {
  onVisibleChanged: null,
  typeMap: {},
};

export default ScheduleDatePicker;
